/**
 * 구독 2채널의 **핸드셰이크 자격**. 서버는 `Sec-WebSocket-Protocol: bearer, <토큰>` 을 요구한다
 * (`ctcells-core` `app/core/auth.py:372-378` — 항목이 **정확히 2개**이고 첫 번째가 `bearer` 여야 한다).
 *
 * 🔴 **토큰을 URL 에 싣지 않는 것이 이 설계의 요점이다.** 쿼리 문자열에 넣으면 프록시 로그 ·
 *    브라우저 히스토리 · Referer 로 샌다. 서브프로토콜은 핸드셰이크 헤더에만 실린다.
 *    그래서 여기서 나온 값은 **로그·에러 메시지 어디에도 찍지 않는다** — 연결 실패는
 *    코드(1006)로만 말하고 자격은 절대 되돌리지 않는다(백엔드 운영규약 §4 와 같은 규칙).
 *
 * 🔴 **`@ctcells/realtime` 은 `@ctcells/api` 를 모른다.** 토큰 보관은 api(전송 계층)의 책임이고
 *    이 패키지는 소켓만 안다. 그래서 값을 가져오는 방법을 **주입**받는다 —
 *    `configureSessionTeardown` 과 같은 결의 등록 방식이고, 부르는 자리는 앱 셸(`AppRuntime`)
 *    하나다. 두 패키지가 서로를 import 하지 않는다.
 *
 * 🔴 **함수를 받는다(값이 아니다).** 값으로 받으면 모듈 로드 시점의 토큰이 갇혀서
 *    **재연결할 때 만료된 토큰을 다시 쓴다.** 해석은 매 연결 시도마다 한다.
 */

/** 지금 쓸 access 토큰. 없으면(로그인 전 · 갱신 중) `null` */
export type StreamAuthResolver = () => string | null

/** 서버가 요구하는 첫 번째 서브프로토콜 항목. 값·순서 둘 다 규약이다 */
export const STREAM_AUTH_TAG = 'bearer'

let resolver: StreamAuthResolver | null = null

/**
 * 토큰 해석기를 등록한다. 앱 셸이 한 번 부르고, 셸이 내려갈 때 `null` 로 지운다.
 * 등록하지 않으면 연결을 **시도하지 않는다**(아래 `streamProtocols` 가 `null` 을 낸다).
 */
export function configureStreamAuth(next: StreamAuthResolver | null): void {
  resolver = next
}

/**
 * 이번 연결에 쓸 서브프로토콜 목록. **붙을 수 없으면 `null`** 이다.
 *
 * 🔴 `null` 은 「연결하지 마라」는 뜻이다. 로그인 전에 소켓을 열면 서버가 핸드셰이크를
 *    거절해(1006) 재연결만 쌓인다 — 열지 않는 쪽이 맞다. 상태는 그대로 stale 로 남고,
 *    그래서 상태변경 버튼은 계속 비활성이다(§6 규약은 느슨해지지 않는다).
 */
export function streamProtocols(): readonly string[] | null {
  const token = resolver?.() ?? null
  if (token === null || token === '') return null
  return [STREAM_AUTH_TAG, token]
}
